import {
  stockpileVisualizationAtom,
  stockpileVisualizationVisibleAtom,
} from '@/atoms/stockpile.atom';
import { evalBestFitPlane } from '@/lib/stockpile/pointInPolygon';
import { Line } from '@react-three/drei';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import * as THREE from 'three';

/**
 * In-canvas visualization of the most recent stockpile compute. Two passes:
 *
 *  1. **Base plane disc** — the polygon footprint triangulated as a fan and
 *     pinned to the best-fit plane. Lets the user eyeball whether the base
 *     plane sits sensibly on the ground or if it's tilted (which usually
 *     means a perimeter vertex landed on the pile / on a curb).
 *
 *  2. **Height columns** — one line segment per sampled cell, from `(cx, cy,
 *     baseZ)` up to `(cx, cy, topZ)`, packed into a single geometry so the
 *     entire 40 000-column field renders in one draw call. Colour ramps from
 *     cool (low) to warm (high) using vertex colours, so the user sees the
 *     height distribution at a glance.
 *
 * Why a single LineSegments and not InstancedMesh of cubes: cubes would chew
 * up fill rate at this density; thin lines are cheap, easy to read, and don't
 * occlude the underlying point cloud.
 *
 * Auto-hides when the panel clears the viz atom (cancel / discard / save) or
 * when the user toggles the visibility checkbox.
 */

/**
 * 5-stop ramp: blue → cyan → green → yellow → red. Short pile = cool, tall =
 * hot. Wider chromatic spread than the original blue→cyan→orange ramp — much
 * easier to read height *bands* on a dense column field.
 *
 * **Colour-space note**: the channel values are produced via `THREE.Color`,
 * which decodes hex literals from sRGB into the renderer's internal linear
 * working space. Geometry color attributes are read by Three.js as already-
 * linear, so writing `tmpColor.r/.g/.b` directly into the Float32 buffer
 * yields the right output sRGB after the renderer's gamma encoding step. An
 * earlier version wrote sRGB-decoded normalised hex values directly (e.g.
 * `0.118` for `#1e40af`); Three.js then double-gamma-encoded them and the
 * warm end of the ramp washed out, leaving the columns looking uniformly
 * blue.
 */
const RAMP_STOPS = [
  new THREE.Color('#1e3a8a'), // 0.0 — deep blue (low)
  new THREE.Color('#22d3ee'), // 0.25 — cyan
  new THREE.Color('#22c55e'), // 0.5 — green
  new THREE.Color('#eab308'), // 0.75 — yellow
  new THREE.Color('#ef4444'), // 1.0 — red (high)
];

const sampleHeightRamp = (t: number, out: THREE.Color): void => {
  const u = Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0;
  // Find the segment u falls in (5 stops → 4 segments).
  const seg = Math.min(RAMP_STOPS.length - 2, Math.floor(u * (RAMP_STOPS.length - 1)));
  const local = u * (RAMP_STOPS.length - 1) - seg;
  out.lerpColors(RAMP_STOPS[seg], RAMP_STOPS[seg + 1], local);
};

const BASE_PLANE_COLOR = '#3b82f6'; // blue-500 — clearly distinct from the orange polygon
const POLYGON_OUTLINE_COLOR = '#fb923c'; // orange-400 — matches StockpileTool

export const StockpileVisualization = () => {
  const viz = useAtomValue(stockpileVisualizationAtom);
  const visible = useAtomValue(stockpileVisualizationVisibleAtom);

  /**
   * Triangulate the polygon footprint into the base-plane disc.
   *
   * Earlier versions used a fan triangulation from vertex 0. That works for
   * convex / star-shaped polygons (which the click-pick flow's auto-sort
   * guarantees) BUT fails for concave outlines — fan triangles cross over
   * the polygon's concave portions and spill outside the real footprint,
   * producing the "amorph shape" you saw on TIN inputs.
   *
   * Fix: use `THREE.ShapeGeometry`, which delegates to earcut. Earcut is
   * concave-safe and handles arbitrary simple polygons correctly.
   *
   * Coordinate trick: ShapeGeometry triangulates a 2-D `Shape` and emits
   * Z=0 for every vertex. We then walk the position attribute and replace
   * each vertex's Z with the best-fit base-plane elevation at its XY, so
   * the disc tracks the same plane the volume integration used.
   *
   * Float32 precision: positions stay in mesh-local coords (relative to the
   * first ring vertex) so they don't truncate at UTM-scale magnitudes; the
   * mesh's `position` carries the world translation.
   *
   * Orientation: earcut wants the outer ring counter-clockwise. We compute
   * the signed area via shoelace and reverse the ring if it's clockwise.
   * The original `viz.polygonRing` order is set by either the picker
   * (auto-sorted CCW) or `extractOuterBoundary` (whichever direction the
   * adjacency walk chose) — we can't trust either to be uniformly CCW.
   */
  const basePlaneGeometry = useMemo(() => {
    if (!viz || viz.polygonRing.length < 3) return null;
    const ring = viz.polygonRing;

    // Mesh-local origin = first ring vertex projected onto the base plane.
    // Keeps the geometry's positions near the origin for Float32 precision.
    const origin = new THREE.Vector3(
      ring[0][0],
      ring[0][1],
      evalBestFitPlane(viz.basePlane, ring[0][0], ring[0][1]),
    );

    // Shoelace signed area in XY. Positive ⇒ CCW (earcut-friendly), negative
    // ⇒ CW (we reverse). Computed in mesh-local XY to keep magnitudes small;
    // the sign is the same as in world coords so the test result is identical.
    let signedArea2 = 0;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      signedArea2 += (a[0] - origin.x) * (b[1] - origin.y) - (b[0] - origin.x) * (a[1] - origin.y);
    }
    const orderedRing = signedArea2 < 0 ? [...ring].reverse() : ring;

    // Build the Shape in mesh-local XY.
    const shape = new THREE.Shape();
    shape.moveTo(orderedRing[0][0] - origin.x, orderedRing[0][1] - origin.y);
    for (let i = 1; i < orderedRing.length; i++) {
      shape.lineTo(orderedRing[i][0] - origin.x, orderedRing[i][1] - origin.y);
    }
    shape.closePath();

    // ShapeGeometry triangulates via earcut (concave-safe). Default
    // `curveSegments` value is fine for our straight-edge polygon.
    const g = new THREE.ShapeGeometry(shape);

    // Replace each triangulated vertex's Z (defaults to 0 in the XY plane)
    // with the best-fit base-plane elevation at that vertex's world XY,
    // expressed in mesh-local Z.
    const positionAttr = g.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < positionAttr.count; i++) {
      const lx = positionAttr.getX(i);
      const ly = positionAttr.getY(i);
      const wx = lx + origin.x;
      const wy = ly + origin.y;
      const wz = evalBestFitPlane(viz.basePlane, wx, wy);
      positionAttr.setZ(i, wz - origin.z);
    }
    positionAttr.needsUpdate = true;
    g.computeVertexNormals();
    return { geometry: g, origin };
  }, [viz]);

  const basePlaneOutlinePoints = useMemo(() => {
    if (!viz || viz.polygonRing.length < 3) return null;
    const closed = [...viz.polygonRing, viz.polygonRing[0]];
    return closed.map(([x, y]) => new THREE.Vector3(x, y, evalBestFitPlane(viz.basePlane, x, y)));
  }, [viz]);

  const columnsGeometry = useMemo(() => {
    if (!viz || viz.cells.length === 0) return null;

    // Skip cells that contributed zero height — they'd render as zero-length
    // single-pixel dots all clustered at the bottom of the ramp, which both
    // (a) wastes draw bandwidth and (b) visually drowns the actual pile
    // gradient with a sea of blue dots. The base-plane disc still shows the
    // polygon footprint so the user still sees what was sampled.
    const heightCells = viz.cells.filter((c) => c.dz > 1e-6);
    if (heightCells.length === 0) return null;

    // Subsample for huge grids — 200k+ columns would still render fine on
    // most GPUs, but the visual becomes a solid block of colour with no
    // useful information. Cap at ~30k visible columns; sample every Nth.
    const maxColumns = 30_000;
    const stride = Math.max(1, Math.ceil(heightCells.length / maxColumns));
    const visibleCells = stride === 1 ? heightCells : heightCells.filter((_, i) => i % stride === 0);

    // Recenter on the polygon's centroid so the LineSegments mesh is positioned
    // somewhere near origin, keeping float32 attribute precision (same reason
    // as the basePlane mesh above).
    let cx0 = 0;
    let cy0 = 0;
    for (const c of visibleCells) {
      cx0 += c.x;
      cy0 += c.y;
    }
    cx0 /= visibleCells.length;
    cy0 /= visibleCells.length;

    const positions = new Float32Array(visibleCells.length * 6);
    const colors = new Float32Array(visibleCells.length * 6);
    const denom = viz.maxHeight > 0 ? viz.maxHeight : 1;

    // Reusable scratch colour — populated per-cell by sampleHeightRamp, then
    // copied into the buffer. Saves allocating a new THREE.Color per point.
    const tmp = new THREE.Color();

    for (let i = 0; i < visibleCells.length; i++) {
      const c = visibleCells[i];
      const px = c.x - cx0;
      const py = c.y - cy0;
      positions[i * 6 + 0] = px;
      positions[i * 6 + 1] = py;
      positions[i * 6 + 2] = c.baseZ;
      positions[i * 6 + 3] = px;
      positions[i * 6 + 4] = py;
      positions[i * 6 + 5] = c.topZ;
      sampleHeightRamp(c.dz / denom, tmp);
      colors[i * 6 + 0] = tmp.r;
      colors[i * 6 + 1] = tmp.g;
      colors[i * 6 + 2] = tmp.b;
      colors[i * 6 + 3] = tmp.r;
      colors[i * 6 + 4] = tmp.g;
      colors[i * 6 + 5] = tmp.b;
    }

    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return { geometry: g, origin: new THREE.Vector3(cx0, cy0, 0), stride };
  }, [viz]);

  if (!viz || !visible) return null;

  return (
    <>
      {/* Translucent base-plane mesh — shows where the best-fit base sits. */}
      {basePlaneGeometry && (
        <mesh
          geometry={basePlaneGeometry.geometry}
          position={basePlaneGeometry.origin.toArray()}
          renderOrder={Number.MAX_SAFE_INTEGER - 250}>
          <meshBasicMaterial
            color={BASE_PLANE_COLOR}
            transparent
            opacity={0.18}
            side={THREE.DoubleSide}
            depthTest={false}
          />
        </mesh>
      )}

      {/* Crisp blue outline of the polygon at base elevation, so the user can
          see the base outline distinctly from the orange polygon outline of
          the picker (which sits at the surface). */}
      {basePlaneOutlinePoints && (
        <Line
          points={basePlaneOutlinePoints}
          color={BASE_PLANE_COLOR}
          lineWidth={2}
          transparent
          opacity={0.75}
          depthTest={false}
        />
      )}

      {/* Per-cell height columns — one batched LineSegments. */}
      {columnsGeometry && (
        <lineSegments
          geometry={columnsGeometry.geometry}
          position={columnsGeometry.origin.toArray()}
          renderOrder={Number.MAX_SAFE_INTEGER - 240}>
          <lineBasicMaterial
            vertexColors
            transparent
            opacity={0.7}
            depthTest={false}
            // Constant line width (LineBasicMaterial doesn't support
            // GL_LINES wider than 1 on most platforms — that's fine here,
            // we want a fine field, not chunky bars).
          />
        </lineSegments>
      )}

      {/* Bright outline of the polygon at the *picked* (surface) elevation,
          so the user can compare what they outlined against where the base
          ended up. Drawn last so it sits on top. */}
      {viz.polygonRing.length >= 3 && (
        <Line
          points={[...viz.polygonRing, viz.polygonRing[0]].map(
            ([x, y, z]) => new THREE.Vector3(x, y, z),
          )}
          color={POLYGON_OUTLINE_COLOR}
          lineWidth={2.5}
          transparent
          opacity={0.9}
          depthTest={false}
        />
      )}
    </>
  );
};
